import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  FaEnvelopeOpenText,
  FaArrowRight,
  FaPaperPlane,
  FaSortUp,
  FaSortDown,
  FaSort,
} from "react-icons/fa";
import { Avatar } from "../../OrgUsageAnalytics/components/Avatar";
import {
  fetchPcInactivityOverview,
  fetchPcInactivityTemplate,
  updatePcInactivityTemplate,
  sendTestPcInactivityEmail,
  bulkSendPcInactivity,
} from "../api";
import { formatRelative } from "../format";
import OutreachModal from "./OutreachModal";
import {
  SORT_COLUMNS,
  sortRowsByColumn,
  buildPreviewTokens,
  describeIncompleteScan,
} from "../pcInactivity";

const MIN_DAYS_PRESETS = [
  { value: 0, label: "All" },
  { value: 30, label: "30+ days" },
  { value: 60, label: "60+ days" },
  { value: 90, label: "90+ days" },
];

/**
 * Bulk-send modal. Edits the single shared re-engagement template (reusing the
 * Marketing GrapesEditor), runs a mandatory dry-run preview, then requires an
 * explicit confirmation before emailing the selected orgs. All sends go to
 * every active owner/admin per org; the backend still enforces the cooldown,
 * recipient, active-org, and test/internal safety gates.
 */
/**
 * PC inactivity binding for the shared outreach dialog. Declared at module
 * scope so the object identity is stable across renders — the dialog's hooks
 * depend on it.
 */
const PC_OUTREACH_API = {
  fetchTemplate: (env) => fetchPcInactivityTemplate(env),
  updateTemplate: (template, env) => updatePcInactivityTemplate(template, env),
  sendTest: (payload, env) => sendTestPcInactivityEmail(payload, env),
  bulkSend: (orgIds, options, env) =>
    bulkSendPcInactivity(orgIds, options, env),
};

const PC_OUTREACH_COPY = {
  sendLabel: "Send re-engagement email",
  templateBlurb:
    "Edit and save the shared re-engagement email, preview it, or send",
  tokenHelp: '{{organizationName}}, {{daysSinceLastRun}},',
};

const pcPreviewTokens = (sampleOrg) =>
  buildPreviewTokens({
    organizationName: sampleOrg?.name,
    daysSinceLastRun: sampleOrg?.daysSinceLastRun,
  });

/**
 * Landing-page panel that surfaces the organizations most at risk of churn by
 * processing-checklist inactivity. Active orgs are ranked by days since their
 * last manual PC run. Defaults to orgs that ran and stopped; never-run orgs
 * (measured from their signup baseline) are opt-in, because their day counts
 * are far larger and would otherwise fill the whole table. Operators can
 * change the minimum-inactivity filter and sort, select orgs, and send the
 * shared re-engagement email in bulk, or click through to any org's 360 view.
 */
export default function PcInactivityOverviewBlock({ env, onSelect }) {
  const [minDaysSince, setMinDaysSince] = useState(30);
  const [sort, setSort] = useState({ key: SORT_COLUMNS.DAYS_SINCE, dir: "desc" });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [rows, setRows] = useState([]);
  const [incomplete, setIncomplete] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [selected, setSelected] = useState(() => new Set());
  const [bulkOpen, setBulkOpen] = useState(false);
  const [templateOnlyOpen, setTemplateOnlyOpen] = useState(false);
  const [hideInternal, setHideInternal] = useState(true);
  // Orgs that never ran are measured from signup and reach 500+ days, while
  // orgs that ran and stopped top out near 100 — ranked together the never-run
  // rows fill the whole table and hide every real re-engagement target. Default
  // to the accounts this list exists to surface; the three "never used" tabs
  // already cover non-adopters.
  const [includeNeverRan, setIncludeNeverRan] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    setIncomplete(null);
    fetchPcInactivityOverview(
      {
        minDaysSince: String(minDaysSince),
        limit: "100",
        excludeInternal: String(hideInternal),
        runState: includeNeverRan ? "all" : "ran",
      },
      env,
    )
      .then((json) => {
        if (cancelled) return;
        setRows(Array.isArray(json?.orgs) ? json.orgs : []);
        setIncomplete(describeIncompleteScan(json));
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err?.message || "Failed to load PC inactivity overview");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [env, minDaysSince, hideInternal, includeNeverRan, reloadKey]);

  const sortedRows = useMemo(
    () => sortRowsByColumn(rows, sort.key, sort.dir),
    [rows, sort],
  );

  // Toggle direction when re-clicking the active column; otherwise switch
  // column with a sensible default direction (text asc, numbers desc).
  const applySort = useCallback((key) => {
    setSort((prev) => {
      if (prev.key === key) {
        return { key, dir: prev.dir === "asc" ? "desc" : "asc" };
      }
      return { key, dir: key === SORT_COLUMNS.NAME ? "asc" : "desc" };
    });
  }, []);

  const sortIcon = useCallback(
    (key) => {
      if (sort.key !== key) {
        return <FaSort style={{ fontSize: 10, opacity: 0.4 }} />;
      }
      return sort.dir === "asc" ? (
        <FaSortUp style={{ fontSize: 10 }} />
      ) : (
        <FaSortDown style={{ fontSize: 10 }} />
      );
    },
    [sort],
  );

  // Drop selections that are no longer visible after a filter/sort/reload.
  useEffect(() => {
    setSelected((prev) => {
      const visible = new Set(sortedRows.map((r) => r.organizationId));
      const next = new Set([...prev].filter((id) => visible.has(id)));
      return next.size === prev.size ? prev : next;
    });
  }, [sortedRows]);

  const toggleOne = useCallback((orgId) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(orgId)) {
        next.delete(orgId);
      } else {
        next.add(orgId);
      }
      return next;
    });
  }, []);

  const allSelected =
    sortedRows.length > 0 && selected.size === sortedRows.length;

  const toggleAll = useCallback(() => {
    setSelected((prev) =>
      prev.size === sortedRows.length
        ? new Set()
        : new Set(sortedRows.map((r) => r.organizationId)),
    );
  }, [sortedRows]);

  const orgNameById = useMemo(() => {
    const map = {};
    for (const row of rows) {
      map[row.organizationId] = row.organizationName;
    }
    return map;
  }, [rows]);

  const selectedIds = useMemo(() => Array.from(selected), [selected]);

  // First selected org drives realistic token values in the email preview.
  const sampleOrg = useMemo(() => {
    const first = rows.find((row) => selected.has(row.organizationId));
    if (!first) {
      return null;
    }
    return {
      name: first.organizationName,
      daysSinceLastRun: first.daysSinceLastRun,
    };
  }, [rows, selected]);

  return (
    <div className="oo-landing-block">
      <div className="oo-landing-block-head">
        <FaEnvelopeOpenText /> Processing checklist inactivity
        <div className="oo-seg" role="tablist">
          {MIN_DAYS_PRESETS.map((preset) => (
            <button
              key={preset.value}
              type="button"
              className={`oo-seg-btn ${
                minDaysSince === preset.value ? "oo-seg-btn--on" : ""
              }`}
              onClick={() => setMinDaysSince(preset.value)}
            >
              {preset.label}
            </button>
          ))}
        </div>
        <div className="oo-landing-head-actions">
          <label className="oo-inline-toggle" title="Hide Addy-internal/test orgs">
            <input
              type="checkbox"
              checked={hideInternal}
              onChange={(e) => setHideInternal(e.target.checked)}
            />
            <span>Hide Addy AI orgs</span>
          </label>
          <label
            className="oo-inline-toggle"
            title="Also list orgs that have never run the processing checklist, measured from signup"
          >
            <input
              type="checkbox"
              checked={includeNeverRan}
              onChange={(e) => setIncludeNeverRan(e.target.checked)}
            />
            <span>Include never-ran orgs</span>
          </label>
          <button
            type="button"
            className="oo-mini-btn"
            onClick={() => setTemplateOnlyOpen(true)}
          >
            <FaEnvelopeOpenText style={{ fontSize: 11 }} /> Email template / test
          </button>
        </div>
      </div>
      <p className="oo-landing-sort-hint">
        {includeNeverRan
          ? "Active orgs ranked by days since their last manual processing checklist run, including orgs that never ran one (measured from signup) — surface accounts to re-engage."
          : "Active orgs that ran the processing checklist and stopped, ranked by days since their last manual run — surface accounts to re-engage."}
      </p>

      {selected.size > 0 ? (
        <div className="oo-bulk-bar">
          <span>{selected.size} selected</span>
          <div className="oo-bulk-bar-actions">
            <button
              type="button"
              className="oo-mini-btn"
              onClick={() => setSelected(new Set())}
            >
              Clear
            </button>
            <button
              type="button"
              className="oo-mini-btn oo-mini-btn--primary"
              onClick={() => setBulkOpen(true)}
            >
              <FaPaperPlane style={{ fontSize: 11 }} /> Send to{" "}
              {selected.size} selected
            </button>
          </div>
        </div>
      ) : null}

      {!loading && !error && incomplete ? (
        <div className="oo-landing-incomplete">{incomplete}</div>
      ) : null}

      {error ? (
        <div className="oo-empty-hint">{error}</div>
      ) : loading ? (
        <div className="oo-skel-stack">
          <div className="ou-skel" style={{ height: 40, marginBottom: 8 }} />
          <div className="ou-skel" style={{ height: 40, marginBottom: 8 }} />
          <div className="ou-skel" style={{ height: 40 }} />
        </div>
      ) : sortedRows.length ? (
        <table className="oo-send-table oo-inactivity-table">
          <thead>
            <tr>
              <th className="oo-check-col">
                <input
                  type="checkbox"
                  checked={allSelected}
                  onChange={toggleAll}
                  aria-label="Select all organizations"
                />
              </th>
              <th
                className="oo-sortable"
                onClick={() => applySort(SORT_COLUMNS.NAME)}
              >
                Organization {sortIcon(SORT_COLUMNS.NAME)}
              </th>
              <th
                className="oo-sortable"
                onClick={() => applySort(SORT_COLUMNS.DAYS_SINCE)}
              >
                Days since last run {sortIcon(SORT_COLUMNS.DAYS_SINCE)}
              </th>
              <th
                className="oo-sortable"
                onClick={() => applySort(SORT_COLUMNS.LAST_RUN)}
              >
                Last run {sortIcon(SORT_COLUMNS.LAST_RUN)}
              </th>
              <th
                className="oo-sortable"
                onClick={() => applySort(SORT_COLUMNS.ALERTS)}
              >
                Alerts {sortIcon(SORT_COLUMNS.ALERTS)}
              </th>
              <th />
            </tr>
          </thead>
          <tbody>
            {sortedRows.map((row) => (
              <tr
                key={row.organizationId}
                className={
                  selected.has(row.organizationId) ? "oo-row--selected" : ""
                }
              >
                <td className="oo-check-col">
                  <input
                    type="checkbox"
                    checked={selected.has(row.organizationId)}
                    onChange={() => toggleOne(row.organizationId)}
                    aria-label={`Select ${row.organizationName}`}
                  />
                </td>
                <td>
                  <button
                    type="button"
                    className="oo-inactivity-orgbtn"
                    onClick={() =>
                      onSelect({
                        id: row.organizationId,
                        name: row.organizationName,
                      })
                    }
                    title={row.organizationId}
                  >
                    <Avatar
                      name={row.organizationName}
                      seed={row.organizationId}
                      size="sm"
                    />
                    <span>{row.organizationName}</span>
                  </button>
                </td>
                <td>
                  <strong>{row.daysSinceLastRun ?? "—"}</strong>
                  {row.hasEverRun ? "" : " (never)"}
                </td>
                <td>
                  {row.lastRunAt ? formatRelative(row.lastRunAt) : "Never"}
                </td>
                <td>
                  {row.alertEnabled ? (
                    <span className="oo-chip oo-chip--ok">On</span>
                  ) : (
                    <span className="oo-chip">Off</span>
                  )}
                </td>
                <td>
                  <FaArrowRight style={{ fontSize: 11, opacity: 0.5 }} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <div className="oo-empty-hint">
          No active organizations match this inactivity filter.
        </div>
      )}

      {bulkOpen ? (
        <OutreachModal
          env={env}
          api={PC_OUTREACH_API}
          copy={PC_OUTREACH_COPY}
          buildTokens={pcPreviewTokens}
          orgIds={selectedIds}
          orgNameById={orgNameById}
          sampleOrg={sampleOrg}
          onClose={() => setBulkOpen(false)}
          onSent={() => {
            setSelected(new Set());
            setReloadKey((k) => k + 1);
          }}
        />
      ) : null}

      {templateOnlyOpen ? (
        <OutreachModal
          env={env}
          api={PC_OUTREACH_API}
          copy={PC_OUTREACH_COPY}
          buildTokens={pcPreviewTokens}
          orgIds={[]}
          orgNameById={orgNameById}
          sampleOrg={null}
          onClose={() => setTemplateOnlyOpen(false)}
          onSent={() => setTemplateOnlyOpen(false)}
        />
      ) : null}
    </div>
  );
}
